import api from 'core/util/api';

export type BgpPrefix = string;

export interface FetchBgpSubPrefixesParams {
  prefixes: BgpPrefix[];
  include_covered?: boolean;
  start_date?: number;
  end_date?: number;
}

/**
 * Fetch sub-prefixes for a given prefix
 * @param params - Parameters for the request
 * @returns Promise resolving to array of sub-prefixes
 */
export async function fetchBgpSubPrefixes(params: FetchBgpSubPrefixesParams): Promise<BgpPrefix[]> {
  return api.post('/api/ui/synthetics/tests/results/bgp/sub-prefixes', { data: params });
}

export interface GroupedTimeseries {
  dimension: string;
  name: string;
  values: number[];
}

export interface PrefixReachabilityTimeseries {
  prefix: GroupedTimeseries[];
  origin_asn: GroupedTimeseries[];
  upstream_asn: GroupedTimeseries[];
  second_upstream_asn: GroupedTimeseries[];
}

export interface VantagePointCount {
  ipv4: number;
  ipv6: number;
}

export interface ReachabilityTimeseriesResponse {
  timestamps: number[];
  step_seconds: number;
  timeseries: Record<BgpPrefix, PrefixReachabilityTimeseries>;
  vantage_points: VantagePointCount;
  lookups: {
    asns: Record<string, string>;
  };
}

export interface FetchBgpReachabilityTimeseriesParams {
  prefixes: BgpPrefix[];
  include_covered?: boolean;
  start_date?: number;
  end_date?: number;
  vp_keys?: string[];
}

/**
 * Fetch reachability timeseries for given prefixes
 * @param params - Parameters for the request
 * @returns Promise resolving to reachability timeseries data
 */
export async function fetchBgpReachabilityTimeseries(
  params: FetchBgpReachabilityTimeseriesParams
): Promise<ReachabilityTimeseriesResponse> {
  return api.post('/api/ui/synthetics/tests/results/bgp/reachability-timeseries', { data: params });
}

export interface ASPathGraphNode {
  upstream_asns: Record<number, number>;
  route_ids: number[];
}

export interface ASPathGraphOriginASN {
  vp_count: number;
  rpki_status: number;
}

export interface ASPathGraphPrefixNode {
  prefix: string;
  origin_asns: Record<number, ASPathGraphOriginASN>;
  vp_count: number;
  asn_nodes: Record<number, ASPathGraphNode>;
  route_ids: number[];
}

export interface ASPathGraph {
  timestamp: { seconds: number; nanos?: number };
  prefix_nodes: Record<string, ASPathGraphPrefixNode>;
}

export interface ASPathGraphRoute {
  vp_key: string;
  as_path: string[];
  is_pruned: boolean;
}

export interface ASPathGraphSeries {
  step_seconds: number;
  graphs: ASPathGraph[];
  routes: Record<number, ASPathGraphRoute>;
}

export interface IpLookupInfo {
  geo?: {
    city?: { name?: string };
    country?: { name?: string; code?: string };
    region?: { name?: string };
  };
  asn?: { id: number; name: string };
}

export interface VantagePointInfo {
  dataset: string;
  collector: string;
  peer_asn: number;
  peer_ip: string;
}

export interface EnrichedASPathGraphSeries extends ASPathGraphSeries {
  lookups: {
    asns: Record<string, string>;
    ips: Record<string, IpLookupInfo>;
    dns: Record<string, string>;
    vps: Record<string, VantagePointInfo>;
  };
}

export interface FetchBgpASPathGraphTimeseriesParams {
  prefixes: BgpPrefix[];
  include_covered?: boolean;
  start_date?: number;
  end_date?: number;
  vp_keys?: string[];
  pruning_percentage?: number;
}

/**
 * Fetch AS Path Graph timeseries for given prefixes
 * @param params - Parameters for the request
 * @returns Promise resolving to AS Path Graph timeseries data
 */
export async function fetchBgpASPathGraphTimeseries(
  params: FetchBgpASPathGraphTimeseriesParams
): Promise<EnrichedASPathGraphSeries> {
  return api.post('/api/ui/synthetics/tests/results/bgp/aspath-graph-timeseries', { data: params });
}
